import { useState } from "react";
import { Button, PocketRow, PocketSection } from "@/components/ui";
import { Icons } from "@/components/ui/icons";
import { useT } from "@/i18n";
import { framesGithubMessages } from "@/i18n/messages/frames-github";
import { openExternalUrl } from "@/lib/open-external-url";
import { githubCommitUrl } from "./github-repository";

export type Commit = { sha: string; message: string; time: string };
export const MAX_VISIBLE_COMMITS = 3;

export function CommitsSection({
  commits,
  repositoryUrl,
}: {
  commits: Commit[];
  repositoryUrl?: string;
}) {
  const t = useT(framesGithubMessages);
  const [expanded, setExpanded] = useState(false);
  if (!commits.length) return null;
  const visible = expanded ? commits : commits.slice(0, MAX_VISIBLE_COMMITS);
  const remaining = commits.length - visible.length;
  return (
    <PocketSection title={t("commits")} testId="github-commits">
      {visible.map((commit) => {
        const url = githubCommitUrl(repositoryUrl, commit.sha);
        return (
          <PocketRow
            key={commit.sha}
            testId={`github-commit-${commit.sha}`}
            title={commit.message}
            leading={
              <Icons.GitCommitHorizontal
                className="h-4 w-4"
                aria-hidden="true"
              />
            }
            secondLine={[commit.sha.slice(0, 7), commit.time]
              .filter(Boolean)
              .join(", ")}
            onSelect={
              url
                ? () => {
                    void openExternalUrl(url);
                  }
                : undefined
            }
            hoverActions={
              url ? (
                <Icons.ExternalLink className="h-3 w-3" aria-hidden="true" />
              ) : undefined
            }
          />
        );
      })}
      {remaining > 0 && (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          data-testid="github-commits-show-more"
          onClick={() => setExpanded(true)}
        >
          {t("showMore", { count: remaining })}
        </Button>
      )}
    </PocketSection>
  );
}
